import React, { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Gift, Star, TrendingUp, Award, Check, X, Clock } from "lucide-react";

const Rewards = () => {
  const [rewards, setRewards] = useState([]);
  const [myRedemptions, setMyRedemptions] = useState([]);
  const [userPoints, setUserPoints] = useState(0);
  const [loading, setLoading] = useState(true);
  const [redeeming, setRedeeming] = useState(null);
  const token = localStorage.getItem("token");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [rewardsRes, redemptionsRes, userRes] = await Promise.all([
        axios.get(`${API}/rewards`),
        axios.get(`${API}/rewards/my-redemptions`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setRewards(rewardsRes.data);
      setMyRedemptions(redemptionsRes.data.redemptions || []);
      setUserPoints(userRes.data.points || 0);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load rewards");
    } finally {
      setLoading(false);
    }
  };

  const redeemReward = async (rewardId) => {
    setRedeeming(rewardId);
    try {
      await axios.post(
        `${API}/rewards/${rewardId}/redeem`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success("Reward redeemed successfully!");
      fetchData();
    } catch (error) {
      console.error("Error redeeming reward:", error);
      toast.error(error.response?.data?.detail || "Failed to redeem reward");
    } finally {
      setRedeeming(null);
    }
  };

  const canRedeem = (reward) => {
    return reward.available && userPoints >= reward.cost_points;
  };

  const getRewardTypeColor = (type) => {
    const colors = {
      consultation_discount: "bg-blue-100 text-blue-800",
      premium_day: "bg-purple-100 text-purple-800",
      certificate: "bg-green-100 text-green-800",
      resource_access: "bg-orange-100 text-orange-800",
      mentorship: "bg-pink-100 text-pink-800"
    };
    return colors[type] || "bg-gray-100 text-gray-800";
  };

  const getStatusBadge = (status) => {
    const variants = {
      active: { variant: "default", text: "Active" },
      used: { variant: "secondary", text: "Used" },
      expired: { variant: "outline", text: "Expired" }
    };
    const config = variants[status] || variants.active;
    return <Badge variant={config.variant}>{config.text}</Badge>;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Rewards Store</h1>
          <p className="text-gray-600">Redeem your points for exciting rewards</p>
          
          {/* Points Display */}
          <Card className="mt-4 bg-gradient-to-r from-blue-50 to-purple-50">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Your Points Balance</p>
                  <div className="flex items-center gap-2">
                    <Star className="w-6 h-6 text-yellow-500" />
                    <span className="text-3xl font-bold text-gray-900">{userPoints}</span>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-600">Rewards Redeemed</p>
                  <p className="text-2xl font-bold text-blue-600">{myRedemptions.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="available">
          <TabsList className="mb-6">
            <TabsTrigger value="available">Available Rewards</TabsTrigger>
            <TabsTrigger value="my-rewards">My Rewards</TabsTrigger>
          </TabsList>

          {/* Available Rewards */}
          <TabsContent value="available">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {rewards.filter(r => r.available).map((reward) => {
                const affordable = canRedeem(reward);
                const outOfStock = reward.quantity !== null && reward.quantity <= 0;
                
                return (
                  <Card key={reward.id} className="overflow-hidden hover:shadow-lg transition-shadow">
                    <CardHeader className="bg-gradient-to-br from-blue-50 to-purple-50">
                      <div className="flex items-start justify-between">
                        <div className="text-4xl">{reward.icon}</div>
                        <Badge className={getRewardTypeColor(reward.reward_type)}>
                          {reward.reward_type.replace('_', ' ').toUpperCase()}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="pt-4">
                      <CardTitle className="text-xl mb-2">{reward.title}</CardTitle>
                      <CardDescription className="mb-4">{reward.description}</CardDescription>
                      
                      <div className="mb-4 p-3 bg-yellow-50 rounded-lg">
                        <p className="text-sm font-medium text-gray-700">Reward Value:</p>
                        <p className="text-lg font-bold text-yellow-700">{reward.value}</p>
                      </div>

                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                          <Star className="w-5 h-5 text-yellow-500" />
                          <span className="text-2xl font-bold text-gray-900">{reward.cost_points}</span>
                        </div>
                        {reward.quantity !== null && (
                          <Badge variant="outline">
                            {reward.quantity} left
                          </Badge>
                        )}
                      </div>

                      <Button
                        onClick={() => redeemReward(reward.id)}
                        disabled={!affordable || outOfStock || redeeming === reward.id}
                        className="w-full"
                        variant={affordable && !outOfStock ? "default" : "secondary"}
                      >
                        {redeeming === reward.id ? (
                          "Redeeming..."
                        ) : outOfStock ? (
                          "Out of Stock"
                        ) : !affordable ? (
                          `Need ${reward.cost_points - userPoints} more points`
                        ) : (
                          "Redeem Now"
                        )}
                      </Button>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {rewards.filter(r => r.available).length === 0 && (
              <Card>
                <CardContent className="py-12 text-center">
                  <Gift className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                  <p className="text-gray-600">No rewards available at the moment</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* My Rewards */}
          <TabsContent value="my-rewards">
            <div className="space-y-4">
              {myRedemptions.map((redemption) => (
                <Card key={redemption.id}>
                  <CardContent className="pt-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="text-2xl">🎁</span>
                          <h3 className="font-bold text-lg">{redemption.reward_title}</h3>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-gray-600">
                          <div className="flex items-center gap-1">
                            <Star className="w-4 h-4 text-yellow-500" />
                            <span>{redemption.points_spent} points</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            <span>Redeemed {new Date(redemption.redeemed_at).toLocaleDateString()}</span>
                          </div>
                        </div>
                      </div>
                      <div>
                        {getStatusBadge(redemption.status)}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}

              {myRedemptions.length === 0 && (
                <Card>
                  <CardContent className="py-12 text-center">
                    <Gift className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                    <p className="text-gray-600 mb-2">You haven't redeemed any rewards yet</p>
                    <p className="text-sm text-gray-500">Start earning points and redeem exciting rewards!</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
      <Footer />
    </div>
  );
};

export default Rewards;